/**
 * The deck list's row elements, by card id — and a way to be told when one
 * appears.
 *
 * ADR-067 Decision 6 forbids finding a card's row by asking the document for
 * it: the render that creates a row hands it over instead. That part has always
 * worked. What did not was the reading side.
 *
 * The desktop arrival lives in `DeckDetailPage` while the rows are created by
 * its child `DeckDesktopPanels`, so the two are separated by a mount boundary.
 * A plain `Map` in a ref crosses that boundary in one direction only: the child
 * writes, and nothing tells the parent it happened. The parent's effect could
 * therefore look at the exact commit where the row did not yet exist, return to
 * wait — and never be asked again, because its dependency list (`cards`, the
 * target's index, the card id) contains nothing that changes when rows first
 * mount. That is not a rare interleaving; it is what happens whenever the page
 * is still showing its own skeleton (the deck request outstanding) at the
 * moment the card page arrives. See `DeckToDeckDeepLink.test.tsx`.
 *
 * So the registry announces. A reader that finds nothing leaves its name with
 * `notifyWhenRegistered`, and the registration itself calls back — during the
 * commit that creates the row, before the browser paints. The wait ends because
 * the thing being waited for happened, not because some later render happened
 * to re-run the reader.
 *
 * The mobile feed needs none of this and does not use it: its arrival effect
 * and its rows are in the SAME component, so the commit that first renders the
 * rows is the commit that first runs the effect. It is left exactly as it is.
 */
export interface DeckRowRegistry {
  get(cardId: string): HTMLElement | undefined
  set(cardId: string, el: HTMLElement): void
  delete(cardId: string): void
  /**
   * The element that SCROLLS the rows — handed over by the same render, for
   * the same reason and by the same route as the rows themselves.
   *
   * A row is only ever reachable by moving the thing it sits in, and which
   * thing that is belongs to the layout: on the desktop the card list is its
   * own scrollport (ADR-067 Decision 3 permits exactly this pair of panels),
   * on the phone the rows sit directly in the page and the feed uses the page
   * scroller without asking. So the layout that renders the rows declares it.
   *
   * It is declared, not discovered: nothing walks up from a row looking for the
   * nearest scrollable ancestor. A search would answer differently the moment a
   * wrapper gained an `overflow`, and would answer at all in cases where the
   * right answer is that this layout does not use the registry.
   */
  setScrollport(el: HTMLElement | null): void
  scrollport(): HTMLElement | null
  /**
   * Call `onRegistered` when `cardId`'s row is next registered. One-shot, and
   * one at a time: a second call replaces the first, because there is only ever
   * one arrival in flight and a superseded one must not fire. Returns a cancel
   * so a reader that goes away — or succeeds by another route — takes its
   * request with it.
   */
  notifyWhenRegistered(cardId: string, onRegistered: () => void): () => void
}

export function createDeckRowRegistry(): DeckRowRegistry {
  const rows = new Map<string, HTMLElement>()
  let scroller: HTMLElement | null = null
  let awaitedId: string | null = null
  let onAwaited: (() => void) | null = null

  return {
    get: (cardId) => rows.get(cardId),
    setScrollport(el) { scroller = el },
    scrollport: () => (scroller?.isConnected ? scroller : null),
    set(cardId, el) {
      rows.set(cardId, el)
      if (cardId !== awaitedId) return
      const fire = onAwaited
      // Cleared BEFORE firing: the callback re-reads the registry and may leave
      // a fresh request, which must not be wiped by this one finishing.
      awaitedId = null
      onAwaited = null
      fire?.()
    },
    delete(cardId) {
      rows.delete(cardId)
      // Deliberately not cancelling a pending request here. A row is detached
      // and re-attached on every render of the list (the ref callback is an
      // inline closure, so React reattaches it each time); treating that as
      // "the row is gone" would cancel a wait a microsecond before the thing it
      // waits for.
    },
    notifyWhenRegistered(cardId, onRegistered) {
      awaitedId = cardId
      onAwaited = onRegistered
      return () => {
        if (awaitedId !== cardId) return
        awaitedId = null
        onAwaited = null
      }
    },
  }
}
